import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Monitor, Cpu, HardDrive, Building2, MapPin, Calendar, FileText, Info, Loader2, CheckCircle, AlertCircle, Wifi, Printer, BatteryCharging, Network } from 'lucide-react';

function getCategoriaEquipo(tipo) {
  if (['PC', 'Notebook', 'MiniPC', 'Servidor', 'Tablet'].includes(tipo)) return 'computador';
  if (['Router', 'AP', 'Switch', 'Firewall'].includes(tipo)) return 'red';
  if (tipo === 'Impresora') return 'impresora';
  if (tipo === 'UPS') return 'ups';
  return 'generico';
}

export default function EquipoPreview({ equipo, onClose }) {
  const [equipoCompleto, setEquipoCompleto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const cargarDetallesEquipo = async () => {
      if (!equipo?.id && !equipo?._id) {
        setError("ID de equipo no válido");
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const equipoId = equipo.id || equipo._id;
        
        const response = await axios.get(`/api/equipos/${equipoId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setEquipoCompleto(response.data);
        setError(null);
      } catch (err) {
        console.error("Error cargando detalles del equipo:", err);
        setEquipoCompleto(equipo);
        setError("No se pudieron cargar todos los detalles");
      } finally {
        setLoading(false);
      }
    };

    cargarDetallesEquipo();
  }, [equipo]);

  if (!equipo) return null;

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      return new Date(fecha).toLocaleDateString('es-PY', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const obtenerEstadoBadge = (estado) => {
    const estados = {
      activo: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      inactivo: { bg: 'bg-gray-100', text: 'text-gray-800', icon: AlertCircle },
      mantenimiento: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertCircle },
      baja: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle }
    };
    return estados[estado?.toLowerCase()] || estados.activo;
  };

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-8">
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin h-8 w-8 text-emerald-600" />
            <span className="ml-3 text-gray-600 font-medium">Cargando detalles del equipo...</span>
          </div>
        </div>
      </div>
    , document.body);
  }

  const equipoData = equipoCompleto || equipo;
  const estadoBadge = obtenerEstadoBadge(equipoData.estado);
  const EstadoIcon = estadoBadge.icon;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-purple-600 px-6 py-5 flex justify-between items-start">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <Monitor className="w-8 h-8 text-white" />
            </div>
            <div className="text-white">
              <h2 className="text-2xl font-bold mb-1">
                {equipoData.nombre || equipoData.descripcion || "Equipo sin nombre"}
              </h2>
              <div className="flex items-center gap-3 text-sm">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${estadoBadge.bg} ${estadoBadge.text} flex items-center gap-1`}>
                  <EstadoIcon className="w-3 h-3" />
                  {(equipoData.estado || 'activo').toUpperCase()}
                </span>
                {equipoData.tipo_equipo && (
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">
                    {equipoData.tipo_equipo.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Información General */}
            <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-xl p-5 border border-emerald-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-emerald-600" />
                Información General
              </h3>
              <div className="space-y-3">
                {equipoData.marca && (
                  <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                    <span className="text-sm text-gray-600 font-medium">Marca</span>
                    <span className="text-sm text-gray-900 font-semibold">{equipoData.marca}</span>
                  </div>
                )}
                {equipoData.modelo && (
                  <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                    <span className="text-sm text-gray-600 font-medium">Modelo</span>
                    <span className="text-sm text-gray-900 font-semibold">{equipoData.modelo}</span>
                  </div>
                )}
                {equipoData.numero_serie && (
                  <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                    <span className="text-sm text-gray-600 font-medium">Número de Serie</span>
                    <span className="text-sm text-gray-900 font-mono font-semibold">{equipoData.numero_serie}</span>
                  </div>
                )}
                {equipoData.sistema_operativo && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-gray-600 font-medium">Sistema Operativo</span>
                    <span className="text-sm text-gray-900 font-semibold">{equipoData.sistema_operativo}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Cliente y Ubicación */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-green-600" />
                Cliente y Ubicación
              </h3>
              <div className="space-y-3">
                {(equipoData.cliente?.nombre || equipoData.cliente_nombre) && (
                  <div className="flex items-start gap-3 py-2 border-b border-green-100">
                    <Building2 className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 font-medium">Cliente</div>
                      <div className="text-sm text-gray-900 font-semibold">
                        {equipoData.cliente?.nombre || equipoData.cliente_nombre}
                      </div>
                    </div>
                  </div>
                )}
                {equipoData.sucursal && (
                  <div className="flex items-start gap-3 py-2 border-b border-green-100">
                    <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 font-medium">Sucursal</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.sucursal}</div>
                    </div>
                  </div>
                )}
                {equipoData.ubicacion && (
                  <div className="flex items-start gap-3 py-2">
                    <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 font-medium">Ubicación Física</div>
                      <div className="text-sm text-gray-900">{equipoData.ubicacion}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Especificaciones Técnicas — dinámicas según tipo */}
            {getCategoriaEquipo(equipoData.tipo_equipo) === 'computador' && (
              <>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-purple-600" />
                    Procesador y Memoria
                    {equipoData.tipo_equipo === 'Tablet' && <span className="ml-auto text-xs font-normal text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">Tablet</span>}
                  </h3>
                  <div className="space-y-3">
                    {equipoData.procesador && (
                      <div className="p-3 bg-white/60 rounded-lg">
                        <div className="text-xs text-gray-500 font-medium mb-1">{equipoData.tipo_equipo === 'Tablet' ? 'Procesador / SoC' : 'Procesador'}</div>
                        <div className="text-sm text-gray-900 font-semibold">{equipoData.procesador}</div>
                      </div>
                    )}
                    {(equipoData.memoria_ram || equipoData.tipo_ram) && (
                      <div className="p-3 bg-white/60 rounded-lg">
                        <div className="text-xs text-gray-500 font-medium mb-1">Memoria RAM</div>
                        <div className="text-sm text-gray-900 font-semibold">
                          {equipoData.memoria_ram || '-'}
                          {equipoData.tipo_ram && <span className="text-gray-600"> ({equipoData.tipo_ram})</span>}
                        </div>
                      </div>
                    )}
                    {equipoData.sistema_operativo && (
                      <div className="p-3 bg-white/60 rounded-lg">
                        <div className="text-xs text-gray-500 font-medium mb-1">Sistema Operativo</div>
                        <div className="text-sm text-gray-900 font-semibold">{equipoData.sistema_operativo}</div>
                      </div>
                    )}
                    {equipoData.pantalla_pulgadas && (
                      <div className="p-3 bg-white/60 rounded-lg">
                        <div className="text-xs text-gray-500 font-medium mb-1">Pantalla</div>
                        <div className="text-sm text-gray-900 font-semibold">{equipoData.pantalla_pulgadas}"</div>
                      </div>
                    )}
                    {equipoData.conectividad && (
                      <div className="p-3 bg-white/60 rounded-lg">
                        <div className="text-xs text-gray-500 font-medium mb-1">Conectividad</div>
                        <div className="text-sm text-gray-900 font-semibold">{equipoData.conectividad}</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <HardDrive className="w-5 h-5 text-orange-600" />
                    Almacenamiento
                  </h3>
                  <div className="space-y-3">
                    {(equipoData.disco || equipoData.tipo_disco) && (
                      <div className="p-3 bg-white/60 rounded-lg">
                        <div className="text-xs text-gray-500 font-medium mb-1">{equipoData.tipo_equipo === 'Tablet' ? 'Almacenamiento Interno' : 'Disco'}</div>
                        <div className="text-sm text-gray-900 font-semibold">
                          {equipoData.disco || '-'}
                          {equipoData.tipo_disco && <span className="text-gray-600"> ({equipoData.tipo_disco})</span>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {getCategoriaEquipo(equipoData.tipo_equipo) === 'red' && (
              <div className="lg:col-span-2 bg-gradient-to-br from-emerald-50 to-emerald-50 rounded-xl p-5 border border-emerald-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Network className="w-5 h-5 text-emerald-600" />
                  Especificaciones de Red
                  <span className="ml-auto text-xs font-normal text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{equipoData.tipo_equipo}</span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {equipoData.puertos_lan && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Puertos LAN</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.puertos_lan}</div>
                    </div>
                  )}
                  {equipoData.protocolo_wifi && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Protocolo WiFi</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.protocolo_wifi}</div>
                    </div>
                  )}
                  {equipoData.frecuencia_wifi && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Banda</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.frecuencia_wifi}</div>
                    </div>
                  )}
                  {equipoData.velocidad_wan && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Velocidad WAN</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.velocidad_wan}</div>
                    </div>
                  )}
                  {equipoData.tipo_switch && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Tipo Switch</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.tipo_switch}</div>
                    </div>
                  )}
                  {equipoData.poe && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">PoE</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.poe}</div>
                    </div>
                  )}
                  {equipoData.firmware_version && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Firmware</div>
                      <div className="text-sm text-gray-900 font-mono font-semibold">{equipoData.firmware_version}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {getCategoriaEquipo(equipoData.tipo_equipo) === 'impresora' && (
              <div className="lg:col-span-2 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-5 border border-teal-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Printer className="w-5 h-5 text-teal-600" />
                  Especificaciones de Impresora
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {equipoData.tipo_impresion && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Tipo de Impresión</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.tipo_impresion}</div>
                    </div>
                  )}
                  {equipoData.conexion_impresora && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Conexión</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.conexion_impresora}</div>
                    </div>
                  )}
                  {equipoData.resolucion_dpi && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Resolución (DPI)</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.resolucion_dpi}</div>
                    </div>
                  )}
                  {equipoData.formato_papel && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Formato Papel</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.formato_papel}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {getCategoriaEquipo(equipoData.tipo_equipo) === 'ups' && (
              <div className="lg:col-span-2 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <BatteryCharging className="w-5 h-5 text-yellow-600" />
                  Especificaciones UPS
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {equipoData.capacidad_va && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Capacidad (VA)</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.capacidad_va} VA</div>
                    </div>
                  )}
                  {equipoData.potencia_w && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Potencia (W)</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.potencia_w} W</div>
                    </div>
                  )}
                  {equipoData.autonomia_min && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Autonomía</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.autonomia_min} min</div>
                    </div>
                  )}
                  {equipoData.tipo_bateria_ups && (
                    <div className="p-3 bg-white/60 rounded-lg">
                      <div className="text-xs text-gray-500 font-medium mb-1">Tipo Batería</div>
                      <div className="text-sm text-gray-900 font-semibold">{equipoData.tipo_bateria_ups}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fechas */}
            {(equipoData.created_at || equipoData.updated_at) && (
              <div className="lg:col-span-2 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  Información de Registro
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {equipoData.created_at && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-200 rounded-lg">
                        <Calendar className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 font-medium">Fecha de Registro</div>
                        <div className="text-sm text-gray-900 font-semibold">{formatearFecha(equipoData.created_at)}</div>
                      </div>
                    </div>
                  )}
                  {equipoData.updated_at && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-200 rounded-lg">
                        <Calendar className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 font-medium">Última Actualización</div>
                        <div className="text-sm text-gray-900 font-semibold">{formatearFecha(equipoData.updated_at)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Observaciones */}
            {equipoData.observaciones && (
              <div className="lg:col-span-2 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-5 border border-teal-100">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-600" />
                  Observaciones
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white/60 p-4 rounded-lg">
                  {equipoData.observaciones}
                </p>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  , document.body);
}
